/* eslint-disable react-hooks/rules-of-hooks */

import { FC, useEffect, useLayoutEffect, useState } from "react";
import { GetNotificationSettingsResponse } from "../../shared/payloads/notificationSettingsPayloads";
import DOMPurify from "dompurify";
import { BorderType } from "../../components/NotificationEditComponents/BorderSelect";
import { NotificationDefaultSettings } from "../../types/notification-default-settings";
import { useTranslation } from "react-i18next";
import { v4 } from "uuid";
import { FormType } from "../../components/types";
import { transformUrlFilterToForms, convertStringToLanguage } from "../../generic-utils";
import { NotificationManager } from "../../services/api/NotificationManager";
import i18n from "../../i18n";
import { Branding } from "../../shared/types/commonTypes";
import Tabs from "../../components/Tabs";
import { NotificationDesignTab } from "../../components/notification/tabs/NotificationDesignTab";
import { NotificationModalBase } from "../../components/notification/NotificationModalBase";
import { NotificationPairingTab } from "../../components/notification/tabs/NotificationPairingTab";
import { addEmptyPairingForm } from "../../utils/forms/addEmptyPairingForm";
import { onAddPairingFormExcludeChip } from "../../utils/forms/onAddPairingFormExcludeChip";
import { onAddPairingFormIncludeChip } from "../../utils/forms/onAddPairingFormIncludeChip";
import { onDeletePairingForm } from "../../utils/forms/onDeletePairingForm";
import { handlePairingFormControlChange } from "../../utils/forms/handlePairingFormControlChange";
import { NotificationRulesTab } from "../../components/notification/tabs/NotificationRulesTab";

interface HeurekaNotificationModalProps {
  createdNotificationId: string;
  isOpen: boolean;
  forOrders: boolean;
  onClose: () => void;
  onSaveSuccess?: () => void;
  data?: GetNotificationSettingsResponse;
  branding: Branding;
}

type TabType = "rules" | "design" | "pairing";

const notificationStyleId = v4();

const HeurekaNotificationModal: FC<HeurekaNotificationModalProps> = ({
  createdNotificationId,
  isOpen,
  onClose,
  onSaveSuccess,
  forOrders,
  data,
  branding,
}) => {
  const { t } = useTranslation();
  const language = convertStringToLanguage(i18n.language);

  const [urlFilterForms, setUrlFilterForms] = useState<FormType[]>(
    data?.urlFilter
      ? transformUrlFilterToForms(data.urlFilter)
      : [{ control: "contains", include: ["/"], excludes: [] }],
  );
  const [minReviewStars, setMinReviewStars] = useState<number>(
    data?.minReviewStars !== undefined ? data?.minReviewStars : 4,
  );
  const [minReviews, setMinReviews] = useState<number>(
    data?.minReviews !== undefined ? data?.minReviews : 1,
  );
  const [withRepeat, setWithRepeat] = useState<boolean>(
    data?.repeatNotification !== undefined ? data?.repeatNotification : true,
  );
  const [selectedTab, setSelectedTab] = useState<TabType>("rules");
  const [notificationHtml, setNotificationHtml] = useState<string>("");

  const [backgroundColor, setBackgroundColor] = useState<string | null>(null);
  const [textColor, setTextColor] = useState<string | null>(null);
  const [iconBackgroundColor, setIconBackgroundColor] = useState<string | null>(
    null,
  );
  const [borderRadius, setBorderRadius] = useState<BorderType | null>(null);

  const [backgroundOpacity, setBackgroundOpracity] = useState<number | null>(
    null,
  );
  const [active, setActive] = useState<"classic" | "small" | "compact">(
    "classic",
  );
  const [showIcon, setShowIcon] = useState<boolean>(true);
  const [iconBackgroundRadius, setIconBackgroundRadius] = useState<
    number | null
  >(null);

  const [previewModalOpen, setPreviewModalOpen] = useState<boolean>(false);
  const [detectedChange, setDetectedChange] = useState<boolean>(false);

  const onCloseAndClearInputs = () => {
    setMinReviewStars(4);
    setMinReviews(1);
    setWithRepeat(true);
    onClose();
  };

  useEffect(() => {
    const defaultBackgroundColor =
      data?.notificationVisualSettings?.backgroundColor ??
      branding?.backgroundColor ??
      NotificationDefaultSettings.BackgroundColor;

    const minReviewStarsDefault =
      data?.minReviewStars !== undefined ? data?.minReviewStars : 4;
    const minReviewDefault =
      data?.minReviews !== undefined ? data?.minReviews : 1;
    const withRepeatDefault =
      data?.repeatNotification !== undefined ? data?.repeatNotification : true;

    const defaultTextColor =
      data?.notificationVisualSettings?.textColor ??
      branding?.textColor ??
      NotificationDefaultSettings.TextColor;

    const activeDefault =
      data?.notificationVisualSettings?.activeSize ?? branding?.activeSize;

    const formsDefault = data?.urlFilter
      ? transformUrlFilterToForms(data.urlFilter)
      : [{ control: "contains", include: ["/"], excludes: [] }];

    const showIconDefault =
      data?.notificationVisualSettings?.isShowingIcon !== null &&
      typeof data?.notificationVisualSettings?.isShowingIcon !== "undefined"
        ? !!data?.notificationVisualSettings?.isShowingIcon
        : branding?.brandingIsShowingIcon !== null &&
          typeof branding?.brandingIsShowingIcon !== "undefined"
        ? !!branding?.brandingIsShowingIcon
        : true;

    const defaultIconBackgroundColor =
      data?.notificationVisualSettings?.iconBackgroundColor ??
      branding?.iconBackgroundColor ??
      iconBackgroundColor ??
      NotificationDefaultSettings.IconBackgroundColor;

    if (
      borderRadius !== null &&
      (data?.notificationVisualSettings?.roundness !== null &&
      data?.notificationVisualSettings?.roundness !== undefined
        ? borderRadius !== data?.notificationVisualSettings?.roundness
        : borderRadius !== branding?.borderRadius)
    ) {
      setDetectedChange(true);
    } else if (
      backgroundOpacity !== null &&
      (data?.notificationVisualSettings?.backgroundOpacity !== null &&
      data?.notificationVisualSettings?.backgroundOpacity !== undefined
        ? backgroundOpacity !==
          data?.notificationVisualSettings?.backgroundOpacity
        : backgroundOpacity !== branding?.backgroundOpacity)
    ) {
      setDetectedChange(true);
    } else if (backgroundColor && backgroundColor !== defaultBackgroundColor) {
      setDetectedChange(true);
    } else if (textColor && textColor !== defaultTextColor) {
      setDetectedChange(true);
    } else if (activeDefault && activeDefault !== active) {
      setDetectedChange(true);
    } else if (showIcon !== showIconDefault) {
      setDetectedChange(true);
    } else if (
      iconBackgroundColor &&
      iconBackgroundColor !== defaultIconBackgroundColor
    ) {
      setDetectedChange(true);
    } else if (
      iconBackgroundRadius &&
      (data?.notificationVisualSettings?.iconBackgroundRadius !== null &&
      data?.notificationVisualSettings?.iconBackgroundRadius !== undefined
        ? iconBackgroundRadius !==
          data?.notificationVisualSettings?.iconBackgroundRadius
        : iconBackgroundRadius !== branding?.iconBackgroundRadius)
    ) {
      setDetectedChange(true);
    } else if (minReviewStars !== minReviewStarsDefault) {
      setDetectedChange(true);
    } else if (minReviews !== minReviewDefault) {
      setDetectedChange(true);
    } else if (withRepeat !== withRepeatDefault) {
      setDetectedChange(true);
    } else if (
      JSON.stringify(urlFilterForms) !== JSON.stringify(formsDefault)
    ) {
      setDetectedChange(true);
    } else {
      setDetectedChange(false);
    }
  }, [
    borderRadius,
    urlFilterForms,
    minReviewStars,
    minReviews,
    withRepeat,
    active,
    textColor,
    backgroundColor,
    backgroundOpacity,
    showIcon,
    iconBackgroundColor,
    iconBackgroundRadius,
    data?.notificationVisualSettings?.iconBackgroundRadius,
    data?.notificationVisualSettings?.iconBackgroundColor,
    data?.notificationVisualSettings?.activeSize,
    data?.notificationVisualSettings?.isShowingIcon,
    data?.notificationVisualSettings?.backgroundColor,
    data?.notificationVisualSettings?.textColor,
    data?.notificationVisualSettings?.backgroundOpacity,
    data?.notificationVisualSettings?.roundness,
    branding?.brandingIsShowingIcon,
    branding?.borderRadius,
    branding?.backgroundOpacity,
    branding?.backgroundColor,
    branding?.textColor,
    branding?.activeSize,
    branding?.iconBackgroundColor,
    branding?.iconBackgroundRadius,
  ]);

  useEffect(() => {
    setBackgroundColor(
      data?.notificationVisualSettings?.backgroundColor ??
        branding?.backgroundColor ??
        null,
    );
    setTextColor(
      data?.notificationVisualSettings?.textColor ??
        branding?.textColor ??
        null,
    );
    setIconBackgroundColor(
      data?.notificationVisualSettings?.iconBackgroundColor ??
        branding?.iconBackgroundColor ??
        null,
    );
    setBorderRadius(
      data?.notificationVisualSettings?.roundness ??
        branding?.borderRadius ??
        null,
    );
    setBackgroundOpracity(
      data?.notificationVisualSettings?.backgroundOpacity ??
        branding?.backgroundOpacity ??
        null,
    );
    setActive(
      data?.notificationVisualSettings?.activeSize ||
        branding?.activeSize ||
        "classic",
    );
    setShowIcon(
      data?.notificationVisualSettings?.isShowingIcon !== null &&
        typeof data?.notificationVisualSettings?.isShowingIcon !== "undefined"
        ? !!data?.notificationVisualSettings?.isShowingIcon
        : branding?.brandingIsShowingIcon !== null &&
          typeof branding?.brandingIsShowingIcon !== "undefined"
        ? !!branding?.brandingIsShowingIcon
        : true,
    );
    setIconBackgroundRadius(
      data?.notificationVisualSettings?.iconBackgroundRadius ??
        branding?.iconBackgroundRadius ??
        null,
    );
  }, [
    data?.notificationVisualSettings?.backgroundColor,
    data?.notificationVisualSettings?.textColor,
    data?.notificationVisualSettings?.iconBackgroundColor,
    data?.notificationVisualSettings?.roundness,
    data?.notificationVisualSettings?.backgroundOpacity,
    data?.notificationVisualSettings?.isShowingIcon,
    data?.notificationVisualSettings?.activeSize,
    data?.notificationVisualSettings?.iconBackgroundRadius,
    branding?.backgroundColor,
    branding?.textColor,
    branding?.iconBackgroundColor,
    branding?.borderRadius,
    branding?.backgroundOpacity,
    branding?.activeSize,
    branding?.brandingIsShowingIcon,
    branding?.iconBackgroundRadius,
  ]);

  useLayoutEffect(() => {
    const createNotificationPreviewHtml = async () => {
      const result = await window.getNotificationPreview(
        {
          campaign: {
            branding: {
              textColor: branding?.textColor,
              backgroundColor: branding?.backgroundColor,
              borderRadius: branding?.borderRadius,
              backgroundOpacity: branding?.backgroundOpacity,
              iconColor: branding?.iconColor,
              iconBackgroundColor: branding?.iconBackgroundColor,
              iconBackgroundRadius: branding?.iconBackgroundRadius,
              activeSize: branding?.activeSize,
              brandingIsShowingIcon: branding?.brandingIsShowingIcon,
              logoDisplay: branding?.logoDisplay,
              logo: branding?.logo,
              logoBackgroundColor: branding?.logoBackgroundColor,
              logoText: branding?.logoText,
            },
            notificationVisualSettings: {
              roundness: 0,
            },
          },
          settings: {
            notificationVisualSettings: {
              backgroundColor:
                backgroundColor ??
                data?.notificationVisualSettings?.backgroundColor ??
                branding?.backgroundColor ??
                "",
              textColor:
                textColor ??
                data?.notificationVisualSettings?.textColor ??
                branding?.textColor ??
                "",
              iconBackgroundColor:
                iconBackgroundColor ??
                data?.notificationVisualSettings?.iconBackgroundColor ??
                branding?.iconBackgroundColor ??
                "",
              iconColor: branding?.iconColor ?? "",
              icon: "",
              roundness:
                borderRadius ??
                data?.notificationVisualSettings?.roundness ??
                branding?.borderRadius ??
                0,
              linkTextColor: "",
              linkButtonBackgroundColor: "",
              backgroundOpacity:
                backgroundOpacity ??
                data?.notificationVisualSettings?.backgroundOpacity ??
                branding?.backgroundOpacity ??
                1,
              activeSize:
                active ??
                data?.notificationVisualSettings?.activeSize ??
                branding?.activeSize ??
                "classic",
              isShowingIcon:
                showIcon ??
                data?.notificationVisualSettings?.isShowingIcon ??
                branding?.brandingIsShowingIcon ??
                true,
              iconBackgroundRadius:
                iconBackgroundRadius ??
                data?.notificationVisualSettings?.iconBackgroundRadius ??
                branding?.iconBackgroundRadius ??
                0,
            },
          },
        },
        "heureka",
        undefined,
        undefined,
        undefined,
        language,
        undefined,
        undefined,
        t("campaign.notifications.heureka.previewText1") ?? "",
        minReviewStars * 20,
        undefined,
        undefined,
        undefined,
        undefined,
        undefined,
        undefined,
        undefined,
        undefined,
        notificationStyleId,
      );
      // Transforms object htmldivelement to pure string
      const cleanHtmlString = DOMPurify.sanitize(result, {
        USE_PROFILES: { html: true, svg: true },
      });
      setNotificationHtml(cleanHtmlString);
    };

    createNotificationPreviewHtml();
  }, [
    branding,
    iconBackgroundColor,
    textColor,
    backgroundColor,
    borderRadius,
    backgroundOpacity,
    iconBackgroundRadius,
    active,
    showIcon,
  ]);

  const handleChange = (event: React.SyntheticEvent, newValue: string) => {
    setSelectedTab(newValue as TabType);
  };

  const onSave = async () => {
    const _notificationManager = NotificationManager.getManager();

    await _notificationManager.updateNotificationSettingsAsync({
      id: createdNotificationId,
      isActive: true,
      repeatNotification: withRepeat,
      minReviews: minReviews,
      minReviewStars: minReviewStars,

      notificationVisualSettings: {
        backgroundColor: backgroundColor ?? branding?.backgroundColor ?? "",
        textColor: textColor ?? branding?.textColor ?? "",
        iconBackgroundColor:
          iconBackgroundColor ?? branding?.iconBackgroundColor ?? "",
        iconColor: branding?.iconColor ?? "",
        icon: "",
        roundness: borderRadius ?? branding?.borderRadius ?? 0,
        linkTextColor: "",
        linkButtonBackgroundColor: "",
        backgroundOpacity:
          backgroundOpacity ?? branding?.backgroundOpacity ?? 1,
        activeSize: active ?? branding?.activeSize ?? "classic",
        isShowingIcon: showIcon ?? branding?.brandingIsShowingIcon ?? true,
        iconBackgroundRadius:
          iconBackgroundRadius ?? branding?.iconBackgroundRadius ?? 0,
      },
    });
    onSaveSuccess?.();
    onCloseAndClearInputs();
  };

  const onReset = () => {
    setActive(
      data?.notificationVisualSettings?.activeSize ??
        branding?.activeSize ??
        "classic",
    );
    setBackgroundColor(null);
    setTextColor(null);
    setBorderRadius(null);
    setBackgroundOpracity(null);
    setShowIcon(
      data?.notificationVisualSettings?.isShowingIcon !== null &&
        typeof data?.notificationVisualSettings?.isShowingIcon !== "undefined"
        ? !!data?.notificationVisualSettings?.isShowingIcon
        : branding?.brandingIsShowingIcon !== null &&
          typeof branding?.brandingIsShowingIcon !== "undefined"
        ? !!branding?.brandingIsShowingIcon
        : true,
    );
    setIconBackgroundColor(null);
    setIconBackgroundRadius(null);
    setMinReviewStars(
      data?.minReviewStars !== undefined ? data?.minReviewStars : 4,
    );
    setMinReviews(data?.minReviews !== undefined ? data?.minReviews : 1);
    setWithRepeat(
      data?.repeatNotification !== undefined ? data?.repeatNotification : true,
    );
    setUrlFilterForms(
      data?.urlFilter
        ? transformUrlFilterToForms(data.urlFilter)
        : [
            {
              control: "beginsWith",
              include: [],
              excludes: [],
            },
          ],
    );
  };

  const tabs = [
    {
      value: "rules",
      label: t("campaign.notifications.settings.displayRules.title"),
      content: (
        <NotificationRulesTab
          rulesType='heureka'
          minReviewStars={minReviewStars}
          minReviews={minReviews}
          withRepeat={withRepeat}
          onChangeMinReviewStars={val => setMinReviewStars(val)}
          onChangeMinReviews={val => setMinReviews(val)}
          onToggleWithRepeat={() => setWithRepeat(!withRepeat)}
          t={t}
        />
      ),
    },
    {
      value: "pairing",
      label: t("campaign.notifications.settings.pairing.title"),
      content: (
        <NotificationPairingTab
          addEmptyPairingForm={addEmptyPairingForm}
          setForms={setUrlFilterForms}
          forms={urlFilterForms}
          forOrders={forOrders}
          handlePairingFormControlChange={handlePairingFormControlChange}
          onAddPairingFormExcludeChip={onAddPairingFormExcludeChip}
          onAddPairingFormIncludeChip={onAddPairingFormIncludeChip}
          onDeletePairingForm={onDeletePairingForm}
        />
      ),
    },
    {
      value: "design",
      label: t("campaign.notifications.settings.design.title"),
      content: (
        <NotificationDesignTab
          active={active}
          data={data}
          backgroundColor={backgroundColor}
          backgroundOpacity={backgroundOpacity}
          borderRadius={borderRadius}
          branding={branding}
          iconBackgroundColor={iconBackgroundColor}
          iconBackgroundRadius={iconBackgroundRadius}
          setActive={setActive}
          setBackgroundColor={setBackgroundColor}
          setBackgroundOpracity={setBackgroundOpracity}
          setBorderRadius={setBorderRadius}
          setIconBackgroundColor={setIconBackgroundColor}
          setIconBackgroundRadius={setIconBackgroundRadius}
          setShowIcon={setShowIcon}
          setTextColor={setTextColor}
          showIcon={showIcon}
          textColor={textColor}
          disableIcon
        />
      ),
    },
  ];

  return (
    <NotificationModalBase
      detectedChange={detectedChange}
      isOpen={isOpen}
      isNewNotification={!data}
      notificationHtml={notificationHtml}
      onClose={onClose}
      onReset={onReset}
      onSave={onSave}
      previewModalOpen={previewModalOpen}
      setPreviewModalOpen={setPreviewModalOpen}
      title={t("campaign.notifications.heureka.title")}
    >
      <Tabs onTabChange={handleChange} selectedTab={selectedTab} tabs={tabs} />
    </NotificationModalBase>
  );
};

export default HeurekaNotificationModal;
